import React from 'react';
import { Modal, Button } from '@aidkitorg/component-library';
import type { ReviewRequiredResult } from '@aidkitorg/aidkit/lib/distro/reviewRequired';

interface ReviewRequiredModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceedToReview: () => void;
  reviewResult: ReviewRequiredResult | null;
}

/**
 * Modal shown when publishing is blocked due to changes requiring review.
 * Currently supports notification-related changes, but designed to be extended
 * for other review reasons (payments, eligibility, etc.) in the future.
 */
export function ReviewRequiredModal({
  isOpen,
  onClose,
  onProceedToReview,
  reviewResult,
}: ReviewRequiredModalProps) {
  const notifications = reviewResult?.reasons.notifications;
  const directlyChanged = notifications?.directlyChanged ?? [];
  const affectedByFieldChanges = notifications?.affectedByFieldChanges ?? [];

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Review Required: Notification Changes Detected"
      size="lg"
    >
      <div className="mb-6">
        <p className="text-gray-700 mb-4">
          Your changes affect enabled notifications. You must review these changes before publishing.
        </p>

        {directlyChanged.length > 0 && (
          <div className="mb-4">
            <h4 className="font-medium text-gray-900 mb-2">
              Directly Modified Notifications ({directlyChanged.length})
            </h4>
            <ul className="list-disc list-inside text-gray-700 text-sm max-h-32 overflow-y-auto">
              {directlyChanged.map((notif) => (
                <li key={notif.targetPrefix}>
                  <span className="font-medium">{notif.name || notif.targetPrefix}</span>
                  {notif.name && <span className="text-gray-500 ml-1">({notif.targetPrefix})</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {affectedByFieldChanges.length > 0 && (
          <div className="mb-4">
            <h4 className="font-medium text-gray-900 mb-2">
              Notifications Affected by Field Changes ({affectedByFieldChanges.length})
            </h4>
            <ul className="list-disc list-inside text-gray-700 text-sm max-h-32 overflow-y-auto">
              {affectedByFieldChanges.map((notif) => (
                <li key={notif.targetPrefix}>
                  <span className="font-medium">{notif.name || notif.targetPrefix}</span>
                  {notif.name && <span className="text-gray-500 ml-1">({notif.targetPrefix})</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className="text-gray-600 text-sm italic">
          Click "Proceed to Review" to be redirected to the distro review page where you can verify these changes.
        </p>
      </div>

      <div className="flex justify-end space-x-3">
        <Button variant="secondary" onClick={onClose}>
          Go Back
        </Button>
        <Button variant="primary" onClick={onProceedToReview}>
          Proceed to Review
        </Button>
      </div>
    </Modal>
  );
}
